// hooks/useTicketAttachments.js
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  adminViewAttachment,
  adminRemoveAttachment,
} from '../../../../services/ticketDetails/admin';
import { ticketQueryKeys } from './useTicketQueries';
import { uploadAttachment, uploadDirectlyToS3 } from '../../../../services/general';

const IMAGE_TYPE_PREFIX = 'image/';
const PDF_TYPE = 'application/pdf';

export const isImageFile = (file) => file.type.startsWith(IMAGE_TYPE_PREFIX);

const isPdfFile = (file) =>
  file.type === PDF_TYPE || (!file.type && file.name?.toLowerCase().endsWith('.pdf'));

export const isAllowedAttachment = (file) => isImageFile(file) || isPdfFile(file);

export function useUploadAttachment({ onSuccess, onError } = {}) {
  return useMutation({
    mutationFn: async ({ file, folderName, rootFolder, onUploadProgress }) => {
      if (!isAllowedAttachment(file)) {
        throw new Error('Only image and PDF files can be attached.');
      }

      // S3 policy matches Content-Type exactly, so always send a real type.
      const contentType = file.type || PDF_TYPE;

      // Step 1 — ask backend for a pre-signed S3 POST policy for this file.
      const { documentId, uploadUrl, uploadFields } = await uploadAttachment({
        fileName: file.name,
        fileSize: file.size,
        contentType,
        folderName,
        rootFolder,
      });

      // Step 2 — upload bytes straight to S3. "file" must be the LAST field
      // in the multipart body per S3's POST policy requirements.
      const formData = new FormData();
      Object.entries(uploadFields).forEach(([key, value]) => formData.append(key, value));
      formData.append('file', file);

      await uploadDirectlyToS3(uploadUrl, formData, onUploadProgress);

      // S3 returns 204 No Content — carry forward what we already know locally.
      return {
        documentId,
        name: file.name,
        fileSize: file.size,
        applicationType: contentType,
      };
    },
    onSuccess,
    onError,
  });
}

export function useViewAttachment({ onError } = {}) {
  return useMutation({
    mutationFn: adminViewAttachment,
    onSuccess: (data) => {
      const url = data?.documentUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    },
    onError,
  });
}

export function useRemoveAttachment({ onSuccess, onError } = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminRemoveAttachment,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ticketQueryKeys.list(variables.ticketId) });
      onSuccess?.(data, variables);
    },
    onError,
  });
}
